import Component from '@glimmer/component';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import { formatAsDollars } from 'tio-common/utils/format';
import {
  PhCheckCircle,
  PhClock,
  PhWarning,
  PhDownloadSimple,
  PhShieldCheck,
} from 'ember-phosphor-icons';
import Card from 'tio-ui/components/card';
import type { PslfPaymentValidation } from 'tio-employee/services/career-pathways-api';

interface PaymentValidationCardSignature {
  Args: {
    payment: PslfPaymentValidation;
    onDownloadProof?: (payment: PslfPaymentValidation) => void;
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

export default class PaymentValidationCard extends Component<PaymentValidationCardSignature> {
  get statusIcon() {
    switch (this.args.payment.status) {
      case 'qualifying':
        return PhCheckCircle;
      case 'pending':
        return PhClock;
      default:
        return PhWarning;
    }
  }

  get chipIntent(): 'success' | 'warning' | 'danger' {
    switch (this.args.payment.status) {
      case 'qualifying':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'danger';
    }
  }

  get formattedDate() {
    return new Date(this.args.payment.paymentDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  get formattedAmount() {
    return formatAsDollars(this.args.payment.amount, { cents: true });
  }

  get passedChecks() {
    return this.args.payment.qualificationChecks.filter((c) => c.passed);
  }

  get failedChecks() {
    return this.args.payment.qualificationChecks.filter((c) => !c.passed);
  }

  handleDownload = () => {
    this.args.onDownloadProof?.(this.args.payment);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Card
      @padding="sm"
      class="hover:shadow-md transition-all duration-200
        {{if (eq @payment.status 'qualifying') 'hover:tio-border-success'}}"
      ...attributes
    >
      <div class="tio-row-top-loose">
        <div class="tio-status-icon tio-status-icon--{{this.chipIntent}}">
          <this.statusIcon
            @size={{24}}
            @weight={{if (eq @payment.status "qualifying") "fill" "regular"}}
          />
        </div>

        <div class="tio-fill-min">
          <div class="tio-row-tight mb-1">
            <Text @size="lg" @weight="semibold">
              {{this.formattedAmount}}
            </Text>
            <Text @size="sm" class="tio-fg-muted">
              paid on
              {{this.formattedDate}}
            </Text>
          </div>

          {{#unless @compact}}
            <div class="mt-3 space-y-1.5">
              {{#each @payment.qualificationChecks as |check|}}
                <div class="tio-row-tight text-sm">
                  {{#if check.passed}}
                    <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0" />
                    <Text class="tio-fg-secondary">
                      <span class="font-medium">{{check.name}}:</span>
                      {{check.reason}}
                    </Text>
                  {{else}}
                    <PhWarning @size={{16}} class="tio-fg-danger shrink-0" />
                    <span class="tio-fg-danger-body">
                      <span class="font-medium">{{check.name}}:</span>
                      {{check.reason}}
                    </span>
                  {{/if}}
                </div>
              {{/each}}
            </div>
          {{/unless}}

          {{#if @compact}}
            <div class="tio-row-tight mt-1">
              <PhShieldCheck @size={{14}} class="tio-fg-success" />
              <Text @size="sm" class="tio-fg-muted">
                {{this.passedChecks.length}}/{{@payment.qualificationChecks.length}}
                checks passed
              </Text>
            </div>
          {{/if}}
        </div>

        <div class="tio-stack-end gap-2 shrink-0">
          <Chip @intent={{this.chipIntent}} @size="sm">
            {{#if (eq @payment.status "qualifying")}}
              Qualifying #{{@payment.paymentNumber}}
            {{else if (eq @payment.status "pending")}}
              Pending Verification
            {{else}}
              Non-Qualifying
            {{/if}}
          </Chip>

          {{#if (eq @payment.status "qualifying")}}
            {{#if @onDownloadProof}}
              <button
                type="button"
                class="tio-fg-brand hover:tio-fg-brand-accent text-sm tio-row-extra-tight transition-colors"
                {{on "click" this.handleDownload}}
              >
                <PhDownloadSimple @size={{16}} />
                Download Proof
              </button>
            {{/if}}
          {{/if}}
        </div>
      </div>
    </Card>
  </template>
}
